/**
 * Global CRM WebSocket client (user-scoped).
 * Owned by PrivateLayout; chat threads subscribe/unsubscribe over the same socket.
 * Text sends can go socket-first via sendWhatsappMessageOverSocket.
 */
import {getCookie} from "../utils/cookies";
import {ACCOUNTS_COOKIE_KEY, ACTIVE_ACCOUNT_COOKIE_KEY} from "../utils/constants";

let socket = null;
let messageHandler = null;
let reconnectTimer = null;
let intentionalClose = false;
let focusedClientId = null;

/** Pending send_message ack waiters keyed by fe_reference_id */
const pendingSendAcks = new Map();

const SEND_ACK_TIMEOUT_MS = 8000;

const getAccessToken = () => {
    const activeId = getCookie(ACTIVE_ACCOUNT_COOKIE_KEY);
    if (!activeId) return null;
    try {
        const accounts = JSON.parse(getCookie(ACCOUNTS_COOKIE_KEY) || "[]");
        const activeAcc = accounts.find(
            (acc) => acc.id.toString() === activeId.toString()
        );
        return activeAcc?.access || null;
    } catch (e) {
        return null;
    }
};

export const getGlobalWebsocketUrl = () => {
    const baseUrl = process.env.REACT_APP_WEBSOCKET_URL;
    const token = getAccessToken();
    if (!baseUrl || !token) return null;
    return `${baseUrl}?token=${token}`;
};

export const setRealtimeMessageHandler = (handler) => {
    messageHandler = handler;
};

export const isRealtimeSocketOpen = () =>
    !!(socket && socket.readyState === WebSocket.OPEN);

export const connectRealtimeSocket = () => {
    intentionalClose = false;
    const url = getGlobalWebsocketUrl();
    if (!url) return null;

    // Already open / connecting
    if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING)
    ) {
        return socket;
    }

    try {
        socket = new WebSocket(url);
    } catch (e) {
        console.error("Realtime socket connect failed", e);
        return null;
    }

    socket.onopen = () => {
        // Re-assert focused chat presence after (re)connect
        if (focusedClientId) {
            sendControl({action: "subscribe_chat", client_id: focusedClientId});
        }
        // Socket healthy again — stop job HTTP polling; rely on campaign.job
        stopAllCampaignJobPolling();
    };

    socket.onmessage = (event) => {
        let payload;
        try {
            payload = JSON.parse(event.data);
        } catch (e) {
            console.error("Realtime socket bad JSON", e);
            return;
        }

        // Resolve socket-first send waiters without double-handling in PrivateLayout
        const eventType = payload?.type;
        if (
            eventType === "whatsapp.send_ack" ||
            eventType === "whatsapp.send_error"
        ) {
            const refId = payload?.data?.fe_reference_id;
            if (refId && pendingSendAcks.has(String(refId))) {
                const waiter = pendingSendAcks.get(String(refId));
                pendingSendAcks.delete(String(refId));
                if (waiter?.timer) clearTimeout(waiter.timer);
                if (eventType === "whatsapp.send_ack") {
                    waiter.resolve(payload.data);
                } else {
                    waiter.reject(payload.data || {error: "send_failed", fallback: true});
                }
                return;
            }
        }

        if (messageHandler) {
            messageHandler(payload);
        }
    };

    socket.onclose = () => {
        socket = null;
        // Fail any in-flight socket sends so FE can fall back to HTTP
        pendingSendAcks.forEach((waiter) => {
            if (waiter?.timer) clearTimeout(waiter.timer);
            waiter.reject({error: "socket_closed", fallback: true, code: "socket_closed"});
        });
        pendingSendAcks.clear();

        // Pending generation jobs: fall back to HTTP poll while socket is down
        startPollingForPendingCampaignJobs();

        if (!intentionalClose) {
            // Simple reconnect backoff
            if (reconnectTimer) clearTimeout(reconnectTimer);
            reconnectTimer = setTimeout(() => {
                connectRealtimeSocket();
            }, 3000);
        }
    };

    socket.onerror = () => {
        // onclose will fire after error; reconnect handled there
    };

    return socket;
};

export const disconnectRealtimeSocket = () => {
    intentionalClose = true;
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    if (socket) {
        try {
            socket.close();
        } catch (e) {
            /* ignore */
        }
        socket = null;
    }
};

const sendControl = (payload) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    try {
        socket.send(JSON.stringify(payload));
        return true;
    } catch (e) {
        return false;
    }
};

export const subscribeChat = (clientId) => {
    if (!clientId) return false;
    focusedClientId = clientId;
    return sendControl({action: "subscribe_chat", client_id: clientId});
};

export const unsubscribeChat = (clientId) => {
    if (!clientId) return false;
    if (focusedClientId && String(focusedClientId) === String(clientId)) {
        focusedClientId = null;
    }
    return sendControl({action: "unsubscribe_chat", client_id: clientId});
};

/**
 * Socket-first text send. Resolves with ack data { fe_reference_id, message }.
 * Rejects with { code, error, fallback } — fallback=true means try HTTP.
 */
export const sendWhatsappMessageOverSocket = (payload, {timeoutMs = SEND_ACK_TIMEOUT_MS} = {}) => {
    return new Promise((resolve, reject) => {
        if (!isRealtimeSocketOpen()) {
            reject({code: "socket_unavailable", error: "Socket not open", fallback: true});
            return;
        }
        const feReferenceId = payload?.fe_reference_id;
        if (!feReferenceId) {
            reject({code: "invalid_payload", error: "fe_reference_id required", fallback: true});
            return;
        }
        const key = String(feReferenceId);
        if (pendingSendAcks.has(key)) {
            reject({code: "duplicate_send", error: "Send already in flight", fallback: false});
            return;
        }

        const timer = setTimeout(() => {
            if (pendingSendAcks.has(key)) {
                pendingSendAcks.delete(key);
                reject({code: "timeout", error: "Socket send timed out", fallback: true});
            }
        }, timeoutMs);

        pendingSendAcks.set(key, {resolve, reject, timer});

        const sent = sendControl({
            action: "send_message",
            client_id: payload.client_id,
            fe_reference_id: feReferenceId,
            type: payload.type || "text",
            message_text: payload.message_text || "",
            ...(payload.reply_id ? {reply_id: payload.reply_id} : {}),
        });

        if (!sent) {
            pendingSendAcks.delete(key);
            clearTimeout(timer);
            reject({code: "socket_unavailable", error: "Failed to write socket", fallback: true});
        }
    });
};

export const getRealtimeSocket = () => socket;

/** Pending campaign.job waiters keyed by job_id */
const pendingCampaignJobs = new Map();

const JOB_POLL_INTERVAL_MS = 10000;
/** Overall wait cap if neither socket nor poll reaches a terminal status */
const JOB_OVERALL_TIMEOUT_MS = 5 * 60 * 1000;

const clearWaiterTimers = (waiter) => {
    if (waiter?.pollTimer) {
        clearInterval(waiter.pollTimer);
        waiter.pollTimer = null;
    }
    if (waiter?.timeoutTimer) {
        clearTimeout(waiter.timeoutTimer);
        waiter.timeoutTimer = null;
    }
};

const settleCampaignJob = (jobId, payload, isError = false) => {
    const key = String(jobId);
    const waiter = pendingCampaignJobs.get(key);
    if (!waiter) return false;
    pendingCampaignJobs.delete(key);
    clearWaiterTimers(waiter);
    if (isError) {
        waiter.reject(payload);
    } else {
        waiter.resolve(payload);
    }
    return true;
};

/** Poll once; settle only on succeeded / failed (ignore queued / running). */
const pollCampaignJobOnce = (jobId, pollFn) => {
    const key = String(jobId);
    if (!pendingCampaignJobs.has(key)) return;
    Promise.resolve(pollFn(jobId))
        .then((body) => {
            const status = body?.status;
            if (status === "succeeded") {
                settleCampaignJob(
                    jobId,
                    {
                        job_id: body.job_id || body.id || jobId,
                        campaign_id: body.campaign?.id,
                        status,
                        error: null,
                        campaign: body.campaign,
                    },
                    false
                );
            } else if (status === "failed") {
                settleCampaignJob(
                    jobId,
                    {
                        job_id: body.job_id || body.id || jobId,
                        campaign_id: body.campaign?.id,
                        status,
                        error: body.error || "Generation failed",
                        campaign: body.campaign,
                    },
                    true
                );
            }
        })
        .catch(() => {
            /* keep waiting; next poll or socket may succeed */
        });
};

/** Start HTTP polling for one waiter (no-op if already polling). */
const startCampaignJobPolling = (waiter) => {
    if (!waiter || waiter.pollTimer || !waiter.pollFn) return;
    pollCampaignJobOnce(waiter.jobId, waiter.pollFn);
    waiter.pollTimer = setInterval(
        () => pollCampaignJobOnce(waiter.jobId, waiter.pollFn),
        JOB_POLL_INTERVAL_MS
    );
};

const stopAllCampaignJobPolling = () => {
    pendingCampaignJobs.forEach((waiter) => {
        if (waiter?.pollTimer) {
            clearInterval(waiter.pollTimer);
            waiter.pollTimer = null;
        }
    });
};

const startPollingForPendingCampaignJobs = () => {
    pendingCampaignJobs.forEach((waiter) => startCampaignJobPolling(waiter));
};

/**
 * Resolve waiters when PrivateLayout receives type=campaign.job
 */
export const handleCampaignJobRealtime = (data) => {
    if (!data?.job_id) return;
    const status = data.status;
    if (status === "succeeded") {
        settleCampaignJob(data.job_id, data, false);
    } else if (status === "failed") {
        settleCampaignJob(data.job_id, data, true);
    }
};

/**
 * Wait for a generation job via socket when connected.
 * If the socket is down (or drops while waiting), poll GET jobs/{id}/ until terminal.
 * @param {string} jobId
 * @param {(jobId: string) => Promise<object>} pollFn returns job body with status (+ campaign)
 */
export const waitForCampaignJob = (jobId, pollFn) => {
    const key = String(jobId);
    return new Promise((resolve, reject) => {
        const waiter = {
            jobId,
            pollFn,
            resolve,
            reject,
            pollTimer: null,
            timeoutTimer: null,
        };
        pendingCampaignJobs.set(key, waiter);

        // Hard cap so we don't hang forever if both paths stall
        waiter.timeoutTimer = setTimeout(() => {
            if (!pendingCampaignJobs.has(key)) return;
            settleCampaignJob(
                jobId,
                {job_id: jobId, status: "failed", error: "Generation timed out"},
                true
            );
        }, JOB_OVERALL_TIMEOUT_MS);

        // Socket down at start → poll immediately; otherwise wait for campaign.job
        if (!isRealtimeSocketOpen()) {
            startCampaignJobPolling(waiter);
        }
    });
};
